import { useMemo } from "react";
import { Container, Skeleton } from "@/components/atoms";
import { ConditionCard } from "@/components/molecules";
import { usePopulatedCategories } from "@/hooks/use-categories";
import { useProducts } from "@/hooks/use-products";

// Shop by Condition -- only show categories that have at least one active product.
// Nothing gets deleted: as products are added, categories auto-appear.
export function ShopByCondition() {
  const { data: categories = [] } = usePopulatedCategories();
  const { data: products = [], isLoading } = useProducts();

  const populatedSlugs = useMemo(
    () => new Set(products.map((p) => p.category)),
    [products],
  );

  const visible = useMemo(
    () =>
      categories
        .filter((c) => c.active && populatedSlugs.has(c.slug))
        .slice(0, 8),
    [categories, populatedSlugs],
  );

  /* CLS — this returned `null` for the whole fetch and then popped a heading plus a card grid in
     from zero height, shoving every section below it down. Measured on the live homepage: the
     sections underneath ("Meet the Founder", "Why DrBurns' ReLeaf") were the named shift sources.
     Reserve the space; still collapse once we KNOW there is nothing to show. */
  if (isLoading) {
    return (
      <section className="py-14">
        <Container>
          <div className="mb-8 flex flex-col items-center gap-2">
            <Skeleton className="h-8 w-56" />
            <Skeleton className="h-5 w-80 max-w-full" />
          </div>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 2 }, (_, i) => (
              <Skeleton key={i} className="aspect-[4/3] w-full" />
            ))}
          </div>
        </Container>
      </section>
    );
  }

  if (!visible.length) return null;

  return (
    <section className="py-14">
      <Container>
        <div className="mb-8 text-center">
          <h2 className="font-condensed text-3xl font-bold text-night-blue">
            Shop by Condition
          </h2>
          <p className="mt-2 text-night-blue/70">
            Formulated to target what you are actually dealing with.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
          {visible.map((c) => (
            <ConditionCard key={c.id} category={c} />
          ))}
        </div>
      </Container>
    </section>
  );
}
